import React from 'react';
import PropTypes from 'prop-types';

import Select from '@decisiv/ui-components/lib/components/Select';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import { H2 } from '@decisiv/ui-components/lib/components/Typography';

import toSelectOptions, { withStoredValue } from '../../shared/selectOptions';
import { stateOptions } from '../states';
import {
  FieldPair,
  FormAlert,
  FormColumn,
  FormColumns,
  PhoneRow,
} from './styles';

// Help text, verbatim from the design (Figma 787:77239).
const JOB_ROLE_HELP =
  'This Job Role will appear by your contact name for additional context.';
const MOBILE_HELP = 'Required for SMS notifications.';
// PD-4684. Matches the Create User wizard's copy for the same field, so the two
// screens describe it the same way.
const AUTHENTICATION_HELP = 'This will determine how the user accesses the app.';
const AUTHENTICATION_LOCKED = (provider) =>
  `${provider} is required for this email domain and cannot be changed.`;

// See states.js for why the State field is not simply narrowed by country.

/**
 * The User Details tab: two columns, as the design lays them out.
 *
 * The section headings are the design's own — "User Information" and "Additional
 * Information" — but note the field grouping is not the one the ticket prose
 * describes. The design is what this follows.
 */
function UserDetailsForm({
  authentication,
  disabled,
  errors,
  onChange,
  options,
  username,
  values,
}) {
  // A server-side message for this field, if it sent one. KeyDS renders it as a
  // warning under the input.
  const problem = (name) => (errors[name] || []).join(', ') || undefined;

  const field = (name, label, extra = {}) => (
    <TextField
      disabled={disabled}
      label={label}
      name={name}
      onChange={(event) => onChange(name, event.target.value)}
      value={values[name]}
      warningMessage={problem(name)}
      withWarningIntent={!!problem(name)}
      {...extra}
    />
  );

  const select = (name, label, items, extra = {}) => (
    <Select
      disabled={disabled}
      label={label}
      name={name}
      onChange={(value) => onChange(name, value)}
      options={toSelectOptions(items)}
      value={values[name]}
      warningMessage={problem(name)}
      withWarningIntent={!!problem(name)}
      {...extra}
    />
  );

  // The country code beside a number, as the design shows one in front of all three.
  // Each has its own column on `users` — mobile_country_code since 2022, phone and
  // fax as of PD-4068.
  //
  // hideClearButton because a code is removed by clearing the number rather than the
  // code: the model drops an orphaned country code on save (see User's
  // clear_phone_country_code), so a code left behind by a deleted number does not
  // survive anyway.
  const numberWithCountryCode = (name, label, { help } = {}) => (
    <PhoneRow>
      {select(`${name}CountryCode`, 'Country Code', options.phoneCountryCodes, {
        hideClearButton: true,
      })}
      {field(name, label, help ? { helpMessage: help } : {})}
    </PhoneRow>
  );

  // The Authentication field explains itself when the admin has no say: a disabled
  // required field with no reason given reads as a fault. UserDetail guarantees the
  // current value is among the options, so the label is there to name.
  const authenticationHelp = () => {
    if (!authentication.locked) return AUTHENTICATION_HELP;

    const current = authentication.options.find(
      ({ value }) => value === values.ssoProvider,
    );

    return AUTHENTICATION_LOCKED(
      current ? current.label : authentication.provider,
    );
  };

  // Messages the server sent that belong to no field on this form — see UserUpdate.
  // Rendered rather than left in state: they are the ones that say why a save was
  // refused outright.
  const refusals = errors.base || [];

  return (
    <>
      {refusals.length > 0 && (
        <FormAlert role="alert">
          {refusals.map((message) => (
            <span key={message}>{message}</span>
          ))}
        </FormAlert>
      )}

      <FormColumns>
        <FormColumn>
          <H2 style={{ margin: 0 }}>User Information</H2>

          {field('firstName', 'First Name', { required: true })}
          {field('lastName', 'Last Name', { required: true })}

          {/* Visible but not editable, which the ticket calls out explicitly: shown
              as a disabled field, not hidden. The controller does not permit it
              either. */}
          <TextField
            disabled
            label="Username"
            name="username"
            onChange={() => {}}
            value={username}
          />

          {/* withStoredValue: job_role is free text with inconsistent spellings, and
              an unrecognised one would render the field blank rather than show it. */}
          {select(
            'jobRole',
            'Job Role',
            withStoredValue(options.jobRoles, values.jobRole),
            { helpMessage: JOB_ROLE_HELP },
          )}
          {field('email', 'Email', { required: true, type: 'email' })}

          {numberWithCountryCode('mobile', 'Mobile Phone Number', {
            help: MOBILE_HELP,
          })}

          {/* PD-4684, last in this column as the Create User wizard puts it last in
              its details step. A domain rule cannot be overridden, so the field is
              disabled and says why. */}
          {select('ssoProvider', 'Authentication', authentication.options, {
            required: true,
            // Not clearable: KeyDS shows the clear button whenever a value is
            // selected, and this field always has one, so clearing it enabled Save
            // on a change that the request then dropped. See editUserService.
            hideClearButton: true,
            disabled: disabled || authentication.locked,
            helpMessage: problem('ssoProvider')
              ? undefined
              : authenticationHelp(),
          })}
        </FormColumn>

        <FormColumn>
          <H2 style={{ margin: 0 }}>Additional Information</H2>

          {numberWithCountryCode('phone', 'Phone')}
          {numberWithCountryCode('fax', 'Fax')}

          {field('address1', 'Address 1')}
          {field('address2', 'Address 2')}

          <FieldPair>
            {select(
              'countryCode',
              'Country',
              withStoredValue(options.countries, values.countryCode),
            )}
            {/* Narrowed by the chosen country, plus whatever is on file — see
                states.js. Puerto Rico genuinely has no subdivisions. */}
            {select(
              'state',
              'State',
              stateOptions(options.states, values.countryCode, values.state),
            )}
          </FieldPair>

          <FieldPair>
            {field('city', 'City')}
            {field('postalCode', 'Postal Code')}
          </FieldPair>
        </FormColumn>
      </FormColumns>
    </>
  );
}

/** As Management::UserDetail sends them: a label to show, a value to persist. */
const OPTION = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const OPTION_LIST = PropTypes.arrayOf(OPTION);

export const FORM_OPTIONS = PropTypes.shape({
  countries: OPTION_LIST,
  jobRoles: OPTION_LIST,
  phoneCountryCodes: OPTION_LIST,
  /** Keyed by country code. */
  states: PropTypes.objectOf(OPTION_LIST),
});

UserDetailsForm.propTypes = {
  /** As Management::UserDetail sends it — what Authentication may be set to. */
  authentication: PropTypes.shape({
    /** 'domain' when an email domain rule decides, and the field is not editable. */
    assignment: PropTypes.string,
    locked: PropTypes.bool,
    provider: PropTypes.string,
    requireSso: PropTypes.bool,
    options: OPTION_LIST,
  }),
  /** True for a viewer without user-management:Modify. */
  disabled: PropTypes.bool,
  /** Server-side messages, keyed by field name. */
  errors: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  onChange: PropTypes.func.isRequired,
  options: FORM_OPTIONS.isRequired,
  /** Shown disabled, and not part of `values` — it is not editable. */
  username: PropTypes.string,
  values: PropTypes.objectOf(PropTypes.string).isRequired,
};

UserDetailsForm.defaultProps = {
  authentication: { locked: false, options: [] },
  disabled: false,
  errors: {},
  username: '',
};

export default UserDetailsForm;
